import {
  Button,
  ButtonProps,
  ComponentWithAs,
  MenuButton,
  MenuButtonProps,
  Select,
  SelectProps,
} from '@chakra-ui/react';
import { ReactNode, useMemo, useRef } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

type MenuWithHotKey = ComponentWithAs<'button', MenuButtonProps> & {
  type: 'menu';
  dropdownHotkey: string;
  children: ReactNode;
};

type SelectWithHotKey = SelectProps & {
  type: 'select';
  dropdownHotkey: string;
};

type ButtonWithHotKey = ButtonProps & {
  type: 'button';
  dropdownHotkey: string;
};

type Props = MenuWithHotKey | SelectWithHotKey | ButtonWithHotKey;

export const WithHotKey = ({ dropdownHotkey, type, children, ...rest }: Props) => {
  const id = useMemo(() => rest.id ?? crypto.randomUUID(), [rest.id]);
  const ref = useRef<HTMLElement>();

  useHotkeys(
    dropdownHotkey,
    () => {
      if (type === 'select') return document.getElementById(id)?.focus();
      if (type === 'button') return document.getElementById(id)?.click();
      document.getElementById(ref.current.id)?.click();
    },
    { preventDefault: true, enabled: !!dropdownHotkey },
  );

  if (type === 'button') {
    return (
      <Button {...(rest as ButtonProps)} id={id}>
        {children}
      </Button>
    );
  }

  if (type === 'select') {
    return (
      <Select {...(rest as SelectProps)} id={id}>
        {children}
      </Select>
    );
  }

  return (
    <MenuButton as={Button} ref={ref} {...(rest as MenuButtonProps)} id={id}>
      {children}
    </MenuButton>
  );
};
